import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from '@/ui';
import { useSessionStore } from '@/auth/session.store';
import { hasAnyPermission, type PermissionCode } from '@/auth/types';
import { marketplaceKeys } from '@/lib/task-query-keys';
import type { OfferRow } from '@/features/tasks/types';
import { fetchOfferPage } from './api';
import { exchangeOfferTabParams, pendingOffersHref } from './tab-params';

/**
 * KAZANMA BILDIRIMI (2026-10-02, K4) — kabukta, yalniz KAZANINCA.
 *
 * Web'e push yok: kazanan buro Tekliflerim'e bakmiyorsa kazandigini ogrenmiyordu, oysa ilk adim
 * ("Odeme alindi") ondan bekleniyor. Kabuk (AppShell) bu hook'u baglar; buro oturumunda, sekme
 * gorunurken 15 sn'de bir Tekliflerim Bekleyenler'in KENDI kapsami okunur ve kabul edilmis
 * satirlarin GOREV kimlikleri izlenir. Yeni gorulen her kimlik icin BIR bildirim:
 * "#812 size verildi" + "Tekliflerim'e git". Kaybetmek aksiyon istemez, bildirilmez.
 *
 * ILK BASARILI OKUMA YALNIZ TOHUMLAR: o an Bekleyenler'de duran kazanimlar ya oturumdan once
 * kazanildi ya da zaten biliniyor; sayfa her acildiginda yeniden bildirmek gurultu olurdu.
 *
 * "Listeden dusen satir" tespiti BILINCLI olarak YOK: satir sure bitimi, sayfa ya da baska bir
 * sebeple de duser ve bildirim yanlis ya da belirsiz olurdu. Yalniz "kabul edilmis olarak
 * GORULDU" sinyali kullanilir.
 */

export const WIN_NOTICE_POLL_MS = 15_000;

/** Bekleyenler kisa bir liste (karar bekleyen + odemesi beklenen); tek sayfa yeter. */
export const WIN_NOTICE_LIMIT = 50;

/** Tekliflerim'in menu kapisi (layout/menus.ts `exchange.offers`): ekrani goremeyen yoklanmaz. */
const OFFER_SCREEN_PERMISSIONS: readonly PermissionCode[] = [
  'createOffer',
  'editOffer',
  'completeTask',
];

export type WinDiff = {
  /** Bu okumada ILK KEZ kabul edilmis gorulen gorev kimlikleri (bildirilecekler). */
  fresh: string[];
  /** Simdiye kadar gorulen kazanimlar — bir sonraki okumanin karsilastirmasi. */
  seen: Set<string>;
};

/**
 * Bir okumadan sonra hangi kazanimlar YENI? `seen === null` ilk basarili okuma: hicbiri yeni
 * sayilmaz, hepsi tohum olur. Kume yalniz BUYUR — satir sekmeden cikip (Odenecekler) geri
 * gelmez, ama gelse bile ikinci bildirim olmaz.
 */
export const diffWins = (rows: readonly OfferRow[], seen: ReadonlySet<string> | null): WinDiff => {
  const won = rows.filter((row) => row.isAccepted).map((row) => row.taskId);
  if (seen === null) return { fresh: [], seen: new Set(won) };
  const fresh = [...new Set(won.filter((taskId) => !seen.has(taskId)))];
  return { fresh, seen: new Set([...seen, ...fresh]) };
};

/** `active`: kabuk bir bolge menusuyle mi cizildi (kapi ekranlarinda yoklama yok). */
export function useWinNotice(active: boolean): void {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snapshot = useSessionStore((state) => state.snapshot);
  const userId = snapshot?.user.id ?? '';
  // Musteri oturumunda HIC istek yok.
  const enabled =
    active &&
    userId !== '' &&
    snapshot?.userType === 'exchange' &&
    hasAnyPermission(snapshot, OFFER_SCREEN_PERMISSIONS);

  const query = useQuery({
    queryKey: marketplaceKeys.winNotice(userId),
    queryFn: () =>
      fetchOfferPage({
        // KENDI kapsami (org kapsami DEGIL): "size verildi" yalniz cagiranin teklifi icin dogru;
        // is arkadasinin kazanimi onun bildirimi. Parametreler Tekliflerim Bekleyenler'inki.
        params: exchangeOfferTabParams('openOffers', { userId, organizationScope: false }),
        limit: WIN_NOTICE_LIMIT,
        offset: 0,
        snapshotAt: null,
      }),
    enabled,
    // Yalniz sekme GORUNURKEN; geri donuste hemen tazele (listelerle ayni, task-server-list.ts).
    refetchInterval: WIN_NOTICE_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // Gorulen kazanimlar KULLANICIYA bagli: oturum degisirse (cikis + baska buro) yeniden tohumlanir.
  const seenRef = useRef<{ userId: string; ids: ReadonlySet<string> } | null>(null);
  const rows = query.data?.items;

  useEffect(() => {
    if (!enabled || rows === undefined) return;
    const previous = seenRef.current?.userId === userId ? seenRef.current.ids : null;
    const { fresh, seen } = diffWins(rows, previous);
    seenRef.current = { userId, ids: seen };
    for (const taskId of fresh) {
      toast({
        message: t('task.winNotice', { id: taskId }),
        tone: 'success',
        // Kendiliginden kapanmaz: bir sonraki adim (Odeme alindi) bu buroda; kacirilmamali.
        duration: 0,
        action: {
          label: t('task.marketResult.goToOffers'),
          onClick: () => {
            void navigate(pendingOffersHref());
          },
        },
      });
    }
  }, [enabled, rows, userId, t, navigate]);
}
